/**
 * BitBoy's 32×32 pixel-art sprite, stored as two text layers so the bangs
 * can move independently of the face. The art is the original BitBoy head,
 * with an outline pixel closing each ear at the left and right edges.
 *
 * FACE legend:  . empty   O outline   S skin   s skin shade   M mouth
 * HAIR legend:  . none    H hair
 *               1 strand that bends late  (flex 1, mid-strand)
 *               2 strand that bends first (flex 2, strand tip)
 *
 * The face layer has skin under every strand, so a bent strand reveals
 * forehead. Each strand pixel is anchored to hair directly above or below
 * it, so a one-pixel sideways bend keeps it attached.
 */

export const SPRITE_SIZE = 32

const FACE = `
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
............ssSSsSSs............
..........sSS.sSssSSSs..........
..........SSSSSSSSSSSS..........
.........sSSSSSSSSSSSSs.........
.....OSsOSSSSSSSSSSSSSSOsSO.....
.....OSssSSSSSSSSSSSSSSssSO.....
......OSsSSSSSSSSSSSSSSsSO......
.......OOSSSSSSssSSSSSSOO.......
........OsSSSSSSSSSSSSsO........
.........OSSSSMMMMSSSSO.........
..........sSSSSSSSSSSs..........
..........OOsSSSSSSsOO..........
............OOssssOO............
................................
................................
................................
................................
................................
................................
`

const HAIR = `
................................
................................
................................
................................
................................
................................
..........HHHHHHHHHHHH..........
.........HHHHHHHHHHHHHH.........
.........HHHHHHHHHHHHHH.........
........HHHHHHHHHHHHHHHH........
.......HHHHHHHHHHHHHHHHHH.......
......HHHHHHHHHHHHHHHHHHHH......
......HHHHHHHHHHHHHHHHHHHH......
......HHHHHH..22.22.HHHHHH......
......HHHH...H........HHHH......
......HHHH............HHHH......
......HHH..............HHH......
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
................................
`

const INK = "#201319"

const PALETTE: Readonly<Record<string, string>> = {
  O: INK,
  M: INK,
  H: INK,
  "1": INK,
  "2": INK,
  S: "#f8cba8",
  s: "#e19e7c",
}

export interface Pixel {
  readonly x: number
  readonly y: number
  readonly color: string
}

export interface Strand extends Pixel {
  readonly flex: 1 | 2
}

function parseLayer(name: string, art: string): Array<Pixel & { char: string }> {
  const rows = art.trim().split("\n")
  if (rows.length !== SPRITE_SIZE || rows.some((r) => r.length !== SPRITE_SIZE)) {
    throw new Error(`BitBoy ${name} layer must be ${SPRITE_SIZE}×${SPRITE_SIZE}`)
  }
  return rows.flatMap((row, y) =>
    [...row].flatMap((char, x) => {
      if (char === ".") return []
      const color = PALETTE[char]
      if (!color) throw new Error(`BitBoy ${name} layer: unknown pixel "${char}"`)
      return [{ x, y, color, char }]
    })
  )
}

const hairPixels = parseLayer("hair", HAIR)

/** Pixels that never move: the face, then the hair mass drawn over it. */
export const BASE_PIXELS: readonly Pixel[] = [
  ...parseLayer("face", FACE),
  ...hairPixels.filter((p) => p.char !== "1" && p.char !== "2"),
]

/** Hair strand pixels, redrawn every frame at their current bend. */
export const STRANDS: readonly Strand[] = hairPixels.flatMap((p) =>
  p.char === "1" || p.char === "2" ? [{ x: p.x, y: p.y, color: p.color, flex: p.char === "2" ? 2 : 1 }] : []
)

/**
 * Sideways shift of a strand pixel for a quantized hair bend in [-2, 2].
 * Tips (flex 2) bend at |bend| ≥ 1, mid-strand pixels (flex 1) join at 2,
 * so a strand curves progressively instead of sliding as one block.
 */
export function strandShift(flex: 1 | 2, bend: number): -1 | 0 | 1 {
  if (Math.abs(bend) < 3 - flex) return 0
  return bend > 0 ? 1 : -1
}

/** Pupil color — the same ink as the outline. */
export const EYE_COLOR = INK

/** Pupil rest positions — each eye is a single pixel on the face. */
export const EYES = [
  { x: 12, y: 17 }, // left eye (viewer's left)
  { x: 19, y: 17 }, // right eye
] as const

/** Closed-eye dash per eye: three pixels centered on the pupil, "- -". */
export const EYES_CLOSED = [
  [
    { x: 11, y: 17 },
    { x: 12, y: 17 },
    { x: 13, y: 17 },
  ],
  [
    { x: 18, y: 17 },
    { x: 19, y: 17 },
    { x: 20, y: 17 },
  ],
] as const
